import { useEffect, useMemo, useState } from "react";

import { authApi } from "../components/api";

const cache = {};
const inflight = {};
const errorCache = {};
const ERROR_TTL_MS = 30000;

const normalizeOption = (option) => ({
  ...option,
  id: option?.id,
  value: option?.value ?? option?.option_value ?? "",
  label: option?.label ?? option?.option_label ?? option?.option_value ?? "",
  active: option?.active ?? option?.is_active ?? true,
  is_default: option?.is_default ?? false,
  metadata: option?.metadata || {},
});

const normalizePayload = (payload, keys) => {
  if (Array.isArray(payload)) {
    return { [keys[0]]: payload.map(normalizeOption) };
  }
  const normalized = {};
  keys.forEach((key) => {
    normalized[key] = Array.isArray(payload?.[key]) ? payload[key].map(normalizeOption) : [];
  });
  return normalized;
};

export function clearMasterDataCache(groupKeys) {
  if (!groupKeys) {
    Object.keys(cache).forEach((key) => delete cache[key]);
    Object.keys(inflight).forEach((key) => delete inflight[key]);
    Object.keys(errorCache).forEach((key) => delete errorCache[key]);
    return;
  }
  const keys = Array.isArray(groupKeys) ? groupKeys : [groupKeys];
  const target = new Set(keys.map((key) => String(key || "").trim().toUpperCase()).filter(Boolean));
  Object.keys(cache).forEach((cacheKey) => {
    const parts = cacheKey.split(",");
    if (parts.some((part) => target.has(part))) {
      delete cache[cacheKey];
    }
  });
  Object.keys(inflight).forEach((cacheKey) => {
    const parts = cacheKey.split(",");
    if (parts.some((part) => target.has(part))) {
      delete inflight[cacheKey];
    }
  });
  Object.keys(errorCache).forEach((cacheKey) => {
    const parts = cacheKey.split(",");
    if (parts.some((part) => target.has(part))) {
      delete errorCache[cacheKey];
    }
  });
}

export function useMasterData(groupKeys) {
  const keys = useMemo(() => {
    const list = Array.isArray(groupKeys) ? groupKeys : [groupKeys];
    return [...new Set(list.map((key) => String(key || "").trim().toUpperCase()).filter(Boolean))];
  }, [groupKeys]);
  const cacheKey = useMemo(() => [...keys].sort().join(","), [keys]);
  const [data, setData] = useState(() => cache[cacheKey] || {});
  const [loading, setLoading] = useState(Boolean(cacheKey) && !cache[cacheKey]);

  useEffect(() => {
    let cancelled = false;
    if (!cacheKey) {
      setData({});
      setLoading(false);
      return undefined;
    }
    if (cache[cacheKey]) {
      setData(cache[cacheKey]);
      setLoading(false);
      return undefined;
    }
    if (errorCache[cacheKey] && Date.now() - errorCache[cacheKey] < ERROR_TTL_MS) {
      setData({});
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const request =
      inflight[cacheKey] ||
      authApi.get(`/master-data/options?group_key=${keys.join(",")}`);
    inflight[cacheKey] = request;
    request
      .then((response) => {
        if (cancelled) return;
        const normalized = normalizePayload(response?.data?.data || {}, keys);
        cache[cacheKey] = normalized;
        delete errorCache[cacheKey];
        setData(normalized);
      })
      .catch(() => {
        errorCache[cacheKey] = Date.now();
        if (!cancelled) setData({});
      })
      .finally(() => {
        delete inflight[cacheKey];
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, keys]);

  const getOptions = (key) => {
    const normalizedKey = String(key || "").trim().toUpperCase();
    return data[normalizedKey] || [];
  };

  const getValues = (key) => getOptions(key).map((option) => option.value);

  return { data, loading, getOptions, getValues };
}
